/**
 * Structured Analysis Report Component
 * Unified card-based layout for ALL video analysis types.
 * Handles both structured JSON (pitcher eval) and markdown (general analysis).
 *
 * Replaces the split rendering paths:
 * - pitcherResult -> inline JSX cards (was lines 1889-2011 in VideoAnalyzer.jsx)
 * - result -> AnalysisReport markdown renderer
 *
 * Now both paths render through the same beautiful card layout.
 *
 * @module StructuredAnalysisReport
 */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import { cn } from '../../../lib/utils';
import {
  exportToJSON,
  exportToCSV,
  exportToPDF,
  exportToMarkdown,
  exportToTXT,
  exportToDOCX,
  exportViaEmail
} from '../../../lib/exportService';
import { AnalysisReport } from './AnalysisReport';
import { XIcon } from './Icons';
import { ReportLanguageExport } from './ReportLanguageExport';

// ─── Color Maps by Analysis Mode ───────────────────────────────────────────────

const MODE_COLOR_MAPS = {
  'pitch-tipping': {
    pitch_tipping:         { border: 'border-red-500/30',    bg: 'bg-red-500/10',    text: 'text-red-400',    label: 'Advanced Scouting' },
    mechanical_efficiency: { border: 'border-blue-500/30',   bg: 'bg-blue-500/10',   text: 'text-blue-400',   label: 'Mechanics' },
    injury_risk:           { border: 'border-orange-500/30', bg: 'bg-orange-500/10',  text: 'text-orange-400', label: 'Injury Risk' },
    pitch_optimization:    { border: 'border-green-500/30',  bg: 'bg-green-500/10',   text: 'text-green-400',  label: 'Pitch Optimization' },
    development:           { border: 'border-purple-500/30', bg: 'bg-purple-500/10',  text: 'text-purple-400', label: 'Development' },
    age_development:       { border: 'border-purple-500/30', bg: 'bg-purple-500/10',  text: 'text-purple-400', label: 'Development' },
  },
  'simple-mechanics': {
    delivery_overview:  { border: 'border-cyan-500/30',   bg: 'bg-cyan-500/10',    text: 'text-cyan-400',   label: 'Delivery Overview' },
    key_mechanics:      { border: 'border-blue-500/30',   bg: 'bg-blue-500/10',    text: 'text-blue-400',   label: 'Key Mechanics' },
    strengths:          { border: 'border-green-500/30',  bg: 'bg-green-500/10',   text: 'text-green-400',  label: 'Strengths' },
    areas_to_watch:     { border: 'border-orange-500/30', bg: 'bg-orange-500/10',  text: 'text-orange-400', label: 'Areas to Watch' },
    quick_take:         { border: 'border-yellow-500/30', bg: 'bg-yellow-500/10',  text: 'text-yellow-400', label: 'Quick Take' },
  },
  'big-league-hitter': {
    temporal_spatial: { border: 'border-green-500/30',  bg: 'bg-green-500/10',   text: 'text-green-400',  label: 'Temporal-Spatial IQ' },
    swing_mechanics:  { border: 'border-blue-500/30',   bg: 'bg-blue-500/10',    text: 'text-blue-400',   label: 'Swing Mechanics' },
    approach_quality: { border: 'border-purple-500/30', bg: 'bg-purple-500/10',  text: 'text-purple-400', label: 'Approach Quality' },
    decision_making:  { border: 'border-cyan-500/30',   bg: 'bg-cyan-500/10',    text: 'text-cyan-400',   label: 'Decision Making' },
    projection:       { border: 'border-yellow-500/30', bg: 'bg-yellow-500/10',  text: 'text-yellow-400', label: 'MLB Projection' },
  },
  'basketball-analysis': {
    offensive_execution: { border: 'border-yellow-500/30', bg: 'bg-yellow-500/10',  text: 'text-yellow-400', label: 'Offensive Execution' },
    defensive_effort:    { border: 'border-blue-500/30',   bg: 'bg-blue-500/10',    text: 'text-blue-400',   label: 'Defensive Effort' },
    movement_quality:    { border: 'border-green-500/30',  bg: 'bg-green-500/10',   text: 'text-green-400',  label: 'Movement Quality' },
    decision_making:     { border: 'border-purple-500/30', bg: 'bg-purple-500/10',  text: 'text-purple-400', label: 'Decision Making' },
    development_areas:   { border: 'border-orange-500/30', bg: 'bg-orange-500/10',  text: 'text-orange-400', label: 'Development Areas' },
  },
  'basketball-biomechanics': {
    joint_mechanics:     { border: 'border-green-500/30',  bg: 'bg-green-500/10',   text: 'text-green-400',  label: 'Joint Mechanics' },
    movement_efficiency: { border: 'border-blue-500/30',   bg: 'bg-blue-500/10',    text: 'text-blue-400',   label: 'Movement Efficiency' },
    injury_risk:         { border: 'border-red-500/30',    bg: 'bg-red-500/10',     text: 'text-red-400',    label: 'Injury Risk' },
    landing_mechanics:   { border: 'border-orange-500/30', bg: 'bg-orange-500/10',  text: 'text-orange-400', label: 'Landing Mechanics' },
    optimization:        { border: 'border-cyan-500/30',   bg: 'bg-cyan-500/10',    text: 'text-cyan-400',   label: 'Optimization' },
  },
  'basketball-shooting': {
    release_mechanics: { border: 'border-yellow-500/30', bg: 'bg-yellow-500/10',  text: 'text-yellow-400', label: 'Release Mechanics' },
    arc_consistency:   { border: 'border-blue-500/30',   bg: 'bg-blue-500/10',    text: 'text-blue-400',   label: 'Arc & Consistency' },
    footwork:          { border: 'border-green-500/30',  bg: 'bg-green-500/10',   text: 'text-green-400',  label: 'Footwork' },
    balance_alignment: { border: 'border-purple-500/30', bg: 'bg-purple-500/10',  text: 'text-purple-400', label: 'Balance & Alignment' },
    shot_selection:    { border: 'border-orange-500/30', bg: 'bg-orange-500/10',  text: 'text-orange-400', label: 'Shot Selection' },
  },
  'basketball-team-defense': {
    rotation_timing:  { border: 'border-blue-500/30',   bg: 'bg-blue-500/10',    text: 'text-blue-400',   label: 'Rotation Timing' },
    help_side:        { border: 'border-green-500/30',  bg: 'bg-green-500/10',   text: 'text-green-400',  label: 'Help-Side Positioning' },
    closeout_quality: { border: 'border-red-500/30',    bg: 'bg-red-500/10',     text: 'text-red-400',    label: 'Closeout Quality' },
    communication:    { border: 'border-purple-500/30', bg: 'bg-purple-500/10',  text: 'text-purple-400', label: 'Communication' },
    scheme_execution: { border: 'border-yellow-500/30', bg: 'bg-yellow-500/10',  text: 'text-yellow-400', label: 'Scheme Execution' },
  },
  'basketball-game-film': {
    offensive_sets:       { border: 'border-yellow-500/30', bg: 'bg-yellow-500/10',  text: 'text-yellow-400', label: 'Offensive Sets' },
    defensive_schemes:    { border: 'border-blue-500/30',   bg: 'bg-blue-500/10',    text: 'text-blue-400',   label: 'Defensive Schemes' },
    transition_play:      { border: 'border-green-500/30',  bg: 'bg-green-500/10',   text: 'text-green-400',  label: 'Transition Play' },
    lineup_effectiveness: { border: 'border-purple-500/30', bg: 'bg-purple-500/10',  text: 'text-purple-400', label: 'Lineup Effectiveness' },
    key_moments:          { border: 'border-red-500/30',    bg: 'bg-red-500/10',     text: 'text-red-400',    label: 'Key Moments' },
  },
  'deep-analysis-baseball': {
    technical_analysis:     { border: 'border-blue-500/30',   bg: 'bg-blue-500/10',    text: 'text-blue-400',   label: 'Technical Analysis' },
    performance_metrics:    { border: 'border-green-500/30',  bg: 'bg-green-500/10',   text: 'text-green-400',  label: 'Performance Metrics' },
    strengths:              { border: 'border-yellow-500/30', bg: 'bg-yellow-500/10',  text: 'text-yellow-400', label: 'Strengths' },
    areas_for_improvement:  { border: 'border-orange-500/30', bg: 'bg-orange-500/10',  text: 'text-orange-400', label: 'Areas for Improvement' },
    recommendations:        { border: 'border-cyan-500/30',   bg: 'bg-cyan-500/10',    text: 'text-cyan-400',   label: 'Recommendations' },
  },
  'deep-analysis-basketball': {
    technical_analysis:     { border: 'border-blue-500/30',   bg: 'bg-blue-500/10',    text: 'text-blue-400',   label: 'Technical Analysis' },
    performance_metrics:    { border: 'border-green-500/30',  bg: 'bg-green-500/10',   text: 'text-green-400',  label: 'Performance Metrics' },
    strengths:              { border: 'border-yellow-500/30', bg: 'bg-yellow-500/10',  text: 'text-yellow-400', label: 'Strengths' },
    areas_for_improvement:  { border: 'border-orange-500/30', bg: 'bg-orange-500/10',  text: 'text-orange-400', label: 'Areas for Improvement' },
    recommendations:        { border: 'border-cyan-500/30',   bg: 'bg-cyan-500/10',    text: 'text-cyan-400',   label: 'Recommendations' },
  },
  'golf-swing': {
    address_setup:       { border: 'border-emerald-500/30', bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Address & Setup' },
    backswing:           { border: 'border-teal-500/30',    bg: 'bg-teal-500/10',    text: 'text-teal-400',    label: 'Backswing' },
    transition_impact:   { border: 'border-green-500/30',   bg: 'bg-green-500/10',   text: 'text-green-400',   label: 'Transition & Impact' },
    follow_through:      { border: 'border-lime-500/30',    bg: 'bg-lime-500/10',    text: 'text-lime-400',    label: 'Follow-Through' },
    overall_assessment:  { border: 'border-cyan-500/30',    bg: 'bg-cyan-500/10',    text: 'text-cyan-400',    label: 'Overall Assessment' },
  },
  'golf-biomechanics': {
    joint_kinematics:    { border: 'border-emerald-500/30', bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Joint Kinematics' },
    ground_reaction:     { border: 'border-teal-500/30',    bg: 'bg-teal-500/10',    text: 'text-teal-400',    label: 'Ground Reaction Forces' },
    center_of_mass:      { border: 'border-green-500/30',   bg: 'bg-green-500/10',   text: 'text-green-400',   label: 'Center of Mass' },
    injury_risk:         { border: 'border-red-500/30',     bg: 'bg-red-500/10',     text: 'text-red-400',     label: 'Injury Risk' },
    efficiency:          { border: 'border-cyan-500/30',    bg: 'bg-cyan-500/10',    text: 'text-cyan-400',    label: 'Biomech Efficiency' },
  },
  'golf-kinematic-sequence': {
    pelvis:              { border: 'border-emerald-500/30', bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Pelvis Segment' },
    thorax:              { border: 'border-teal-500/30',    bg: 'bg-teal-500/10',    text: 'text-teal-400',    label: 'Thorax Segment' },
    lead_arm:            { border: 'border-green-500/30',   bg: 'bg-green-500/10',   text: 'text-green-400',   label: 'Lead Arm Segment' },
    clubhead:            { border: 'border-lime-500/30',    bg: 'bg-lime-500/10',    text: 'text-lime-400',    label: 'Clubhead Segment' },
    sequence_timing:     { border: 'border-cyan-500/30',    bg: 'bg-cyan-500/10',    text: 'text-cyan-400',    label: 'Sequence Timing' },
  },
  'golf-short-game': {
    setup_alignment:     { border: 'border-emerald-500/30', bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Setup & Alignment' },
    technique:           { border: 'border-teal-500/30',    bg: 'bg-teal-500/10',    text: 'text-teal-400',    label: 'Technique' },
    trajectory_control:  { border: 'border-green-500/30',   bg: 'bg-green-500/10',   text: 'text-green-400',   label: 'Trajectory Control' },
    distance_control:    { border: 'border-lime-500/30',    bg: 'bg-lime-500/10',    text: 'text-lime-400',    label: 'Distance Control' },
    touch_feel:          { border: 'border-cyan-500/30',    bg: 'bg-cyan-500/10',    text: 'text-cyan-400',    label: 'Touch & Feel' },
  },
  'golf-course-management': {
    shot_selection:      { border: 'border-emerald-500/30', bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Shot Selection' },
    risk_reward:         { border: 'border-teal-500/30',    bg: 'bg-teal-500/10',    text: 'text-teal-400',    label: 'Risk-Reward' },
    mental_approach:     { border: 'border-green-500/30',   bg: 'bg-green-500/10',   text: 'text-green-400',   label: 'Mental Approach' },
    scoring_zones:       { border: 'border-lime-500/30',    bg: 'bg-lime-500/10',    text: 'text-lime-400',    label: 'Scoring Zones' },
    decision_quality:    { border: 'border-cyan-500/30',    bg: 'bg-cyan-500/10',    text: 'text-cyan-400',    label: 'Decision Quality' },
  },
  'deep-analysis-golf': {
    technical_analysis:     { border: 'border-emerald-500/30', bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Technical Analysis' },
    performance_metrics:    { border: 'border-teal-500/30',    bg: 'bg-teal-500/10',    text: 'text-teal-400',    label: 'Performance Metrics' },
    strengths:              { border: 'border-green-500/30',   bg: 'bg-green-500/10',   text: 'text-green-400',   label: 'Strengths' },
    areas_for_improvement:  { border: 'border-orange-500/30',  bg: 'bg-orange-500/10',  text: 'text-orange-400',  label: 'Areas for Improvement' },
    recommendations:        { border: 'border-cyan-500/30',    bg: 'bg-cyan-500/10',    text: 'text-cyan-400',    label: 'Recommendations' },
  },
  'soccer-tactical': {
    formation_shape:    { border: 'border-blue-500/30', bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Formation' },
    pitch_control:      { border: 'border-sky-500/30',  bg: 'bg-sky-500/10',  text: 'text-sky-400',  label: 'Pitch Control' },
    pressing_patterns:  { border: 'border-indigo-500/30', bg: 'bg-indigo-500/10', text: 'text-indigo-400', label: 'Pressing' },
    build_up_play:      { border: 'border-violet-500/30', bg: 'bg-violet-500/10', text: 'text-violet-400', label: 'Build-Up' },
    overall_tactical:   { border: 'border-cyan-500/30', bg: 'bg-cyan-500/10', text: 'text-cyan-400', label: 'Overall' },
  },
  'soccer-biomechanics': {
    kinematic_load:      { border: 'border-blue-500/30', bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Kinematic Load' },
    fatigue_indicators:  { border: 'border-sky-500/30',  bg: 'bg-sky-500/10',  text: 'text-sky-400',  label: 'Fatigue' },
    injury_risk:         { border: 'border-red-500/30',  bg: 'bg-red-500/10',  text: 'text-red-400',  label: 'Injury Risk' },
    sprint_mechanics:    { border: 'border-indigo-500/30', bg: 'bg-indigo-500/10', text: 'text-indigo-400', label: 'Sprint' },
    movement_efficiency: { border: 'border-cyan-500/30', bg: 'bg-cyan-500/10', text: 'text-cyan-400', label: 'Efficiency' },
  },
  'soccer-attacking': {
    chance_creation:    { border: 'border-blue-500/30', bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Chance Creation' },
    xg_analysis:        { border: 'border-sky-500/30',  bg: 'bg-sky-500/10',  text: 'text-sky-400',  label: 'xG Analysis' },
    off_ball_movement:  { border: 'border-indigo-500/30', bg: 'bg-indigo-500/10', text: 'text-indigo-400', label: 'Off-Ball' },
    pattern_play:       { border: 'border-violet-500/30', bg: 'bg-violet-500/10', text: 'text-violet-400', label: 'Patterns' },
    final_third:        { border: 'border-cyan-500/30', bg: 'bg-cyan-500/10', text: 'text-cyan-400', label: 'Final Third' },
  },
  'soccer-defensive': {
    pressing_organization: { border: 'border-blue-500/30', bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Pressing' },
    block_compactness:     { border: 'border-sky-500/30',  bg: 'bg-sky-500/10',  text: 'text-sky-400',  label: 'Compactness' },
    duels:                 { border: 'border-indigo-500/30', bg: 'bg-indigo-500/10', text: 'text-indigo-400', label: 'Duels' },
    recovery_runs:         { border: 'border-violet-500/30', bg: 'bg-violet-500/10', text: 'text-violet-400', label: 'Recovery' },
    marking_discipline:    { border: 'border-cyan-500/30', bg: 'bg-cyan-500/10', text: 'text-cyan-400', label: 'Marking' },
  },
  'soccer-set-pieces': {
    corner_kicks:      { border: 'border-blue-500/30', bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Corners' },
    free_kicks:        { border: 'border-sky-500/30',  bg: 'bg-sky-500/10',  text: 'text-sky-400',  label: 'Free Kicks' },
    throw_ins:         { border: 'border-indigo-500/30', bg: 'bg-indigo-500/10', text: 'text-indigo-400', label: 'Throw-Ins' },
    defensive_setup:   { border: 'border-violet-500/30', bg: 'bg-violet-500/10', text: 'text-violet-400', label: 'Def. Setup' },
    routine_quality:   { border: 'border-cyan-500/30', bg: 'bg-cyan-500/10', text: 'text-cyan-400', label: 'Routines' },
  },
  'deep-analysis-soccer': {
    technical_analysis:     { border: 'border-blue-500/30',   bg: 'bg-blue-500/10',   text: 'text-blue-400',   label: 'Technical' },
    performance_metrics:    { border: 'border-sky-500/30',    bg: 'bg-sky-500/10',    text: 'text-sky-400',    label: 'Performance' },
    strengths:              { border: 'border-indigo-500/30', bg: 'bg-indigo-500/10', text: 'text-indigo-400', label: 'Strengths' },
    areas_for_improvement:  { border: 'border-orange-500/30', bg: 'bg-orange-500/10', text: 'text-orange-400', label: 'Improvement' },
    recommendations:        { border: 'border-cyan-500/30',   bg: 'bg-cyan-500/10',   text: 'text-cyan-400',   label: 'Recommendations' },
  },
};

// Fallback color cycle for unknown/dynamic dimension names
const FALLBACK_COLORS = [
  { border: 'border-blue-500/30',   bg: 'bg-blue-500/10',   text: 'text-blue-400' },
  { border: 'border-green-500/30',  bg: 'bg-green-500/10',  text: 'text-green-400' },
  { border: 'border-purple-500/30', bg: 'bg-purple-500/10', text: 'text-purple-400' },
  { border: 'border-orange-500/30', bg: 'bg-orange-500/10', text: 'text-orange-400' },
  { border: 'border-cyan-500/30',   bg: 'bg-cyan-500/10',   text: 'text-cyan-400' },
  { border: 'border-red-500/30',    bg: 'bg-red-500/10',    text: 'text-red-400' },
  { border: 'border-yellow-500/30', bg: 'bg-yellow-500/10', text: 'text-yellow-400' },
];

// ─── Score Normalization ───────────────────────────────────────────────────────

function normalizeScore(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number') return Math.min(100, Math.max(0, raw));

  const str = String(raw).trim();

  // "85/100" or "8.5/10"
  const fractionMatch = str.match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+)$/);
  if (fractionMatch) {
    const num = parseFloat(fractionMatch[1]);
    const den = parseFloat(fractionMatch[2]);
    if (den > 0) return Math.min(100, Math.max(0, Math.round((num / den) * 100)));
  }

  // "85%"
  const percentMatch = str.match(/^(\d+(?:\.\d+)?)\s*%$/);
  if (percentMatch) return Math.min(100, Math.max(0, Math.round(parseFloat(percentMatch[1]))));

  // Letter grades
  const gradeMap = {
    'A+': 98, 'A': 95, 'A-': 92,
    'B+': 88, 'B': 85, 'B-': 82,
    'C+': 78, 'C': 75, 'C-': 72,
    'D+': 68, 'D': 65, 'D-': 62,
    'F': 50,
  };
  if (gradeMap[str.toUpperCase()]) return gradeMap[str.toUpperCase()];

  // Plain number
  const plainNum = parseFloat(str);
  if (!isNaN(plainNum) && plainNum >= 0 && plainNum <= 100) return Math.round(plainNum);

  return null;
}

// ─── Markdown-to-Structured Parser ─────────────────────────────────────────────

function parseMarkdownToStructured(markdown, analysisMode) {
  if (!markdown || typeof markdown !== 'string') {
    return { player_name: null, overall_grade: null, executive_summary: null, top_3_action_items: [], dimensions: {} };
  }

  // Handle JSON-wrapped strings (e.g. {"response":"### Basketball..."})
  const trimmed = markdown.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('"')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') {
        const candidates = [
          parsed?.response, parsed?.analysis_result, parsed?.result,
          parsed?.data?.response, parsed?.text, parsed?.content, parsed?.message,
        ];
        const found = candidates.find(c => typeof c === 'string' && c.trim().length > 0);
        if (found) return parseMarkdownToStructured(found, analysisMode);
      }
    } catch (e) { /* not JSON */ }
  }

  const lines = markdown.split('\n');

  // Detect dominant heading level — promote H3 to section level when no H2 found
  const hasH2 = lines.some(l => l.trim().match(/^##\s+[^#]/));
  const hasH3 = lines.some(l => l.trim().match(/^###\s+/));
  const promoteH3 = !hasH2 && hasH3;

  let title = null;
  let overallGrade = null;
  let executiveSummary = null;
  const actionItems = [];
  const sections = [];
  let currentSection = null;

  // First pass: extract title and build sections
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // H1 — title
    const h1Match = trimmed.match(/^#\s+(.+)$/);
    if (h1Match && !title) {
      title = h1Match[1].replace(/\*\*/g, '').trim();
      // Check for score in title: "# Player Analysis (85/100)"
      const titleScore = h1Match[1].match(/\((\d+(?:\.\d+)?)\s*\/\s*(\d+)\)/);
      if (titleScore) overallGrade = normalizeScore(`${titleScore[1]}/${titleScore[2]}`);
      continue;
    }

    // H2 — section start (also H3 when no H2 found in document)
    const h2Match = trimmed.match(/^##\s+(.+)$/) || (promoteH3 ? trimmed.match(/^###\s+(.+)$/) : null);
    if (h2Match) {
      if (currentSection) sections.push(currentSection);
      const heading = h2Match[1].replace(/\*\*/g, '').trim();
      // Check for score in heading: "## Mechanics (85/100)" or "## Mechanics: 85"
      let sectionScore = null;
      const headingScoreMatch = heading.match(/\((\d+(?:\.\d+)?)\s*\/\s*(\d+)\)/);
      const headingScoreMatch2 = heading.match(/:\s*(\d+)\s*$/);
      const headingGradeMatch = heading.match(/:\s*([A-F][+-]?)\s*$/i);
      if (headingScoreMatch) sectionScore = normalizeScore(`${headingScoreMatch[1]}/${headingScoreMatch[2]}`);
      else if (headingScoreMatch2) sectionScore = normalizeScore(headingScoreMatch2[1]);
      else if (headingGradeMatch) sectionScore = normalizeScore(headingGradeMatch[1]);

      currentSection = { heading, content: [], score: sectionScore, badges: [] };
      continue;
    }

    // H3 — sub-section bold label (only when not promoted to section level)
    const h3Match = !promoteH3 && trimmed.match(/^###\s+(.+)$/);
    if (h3Match && currentSection) {
      currentSection.content.push(`**${h3Match[1].replace(/\*\*/g, '').trim()}**`);
      continue;
    }

    // Detect inline scores in content lines
    if (currentSection && currentSection.score == null) {
      const scorePattern = trimmed.match(/(?:score|rating|grade|overall)\s*[:=]\s*(\d+(?:\.\d+)?(?:\s*\/\s*\d+)?|[A-F][+-]?)/i);
      if (scorePattern) {
        currentSection.score = normalizeScore(scorePattern[1]);
      }
    }

    // Detect risk/grade badges
    if (currentSection) {
      const riskMatch = trimmed.match(/\b(CRITICAL|HIGH|MODERATE|MEDIUM|LOW|NONE)\s*(RISK|TIPPING)?/i);
      if (riskMatch) {
        currentSection.badges.push(riskMatch[0].toUpperCase());
      }
    }

    // Detect overall grade patterns in non-section lines
    if (!overallGrade) {
      const overallMatch = trimmed.match(/(?:overall\s+(?:grade|score|rating))\s*[:=]\s*(\d+(?:\.\d+)?(?:\s*\/\s*\d+)?|[A-F][+-]?)/i);
      if (overallMatch) overallGrade = normalizeScore(overallMatch[1]);
    }

    // Collect content
    if (currentSection) {
      currentSection.content.push(line);
    } else if (trimmed.length > 0 && !trimmed.startsWith('#')) {
      // Pre-section content — potential executive summary
      if (!executiveSummary && trimmed.length > 30) {
        executiveSummary = trimmed.replace(/\*\*/g, '');
      }
    }
  }

  if (currentSection) sections.push(currentSection);

  // Second pass: identify executive summary and action items from sections
  for (const section of sections) {
    const headingLower = section.heading.toLowerCase();

    // Executive summary detection
    if (!executiveSummary && (
      headingLower.includes('executive summary') ||
      headingLower.includes('overview') ||
      (headingLower.includes('summary') && !headingLower.includes('dimension'))
    )) {
      executiveSummary = section.content
        .map(l => l.trim())
        .filter(l => l.length > 0 && !l.startsWith('#'))
        .join(' ')
        .replace(/\*\*/g, '')
        .trim();
      section._isExecSummary = true;
    }

    // Action items detection
    if (
      headingLower.includes('action') ||
      headingLower.includes('recommendation') ||
      headingLower.includes('next step') ||
      headingLower.includes('immediate') ||
      headingLower.includes('correction') ||
      headingLower.includes('key takeaway') ||
      headingLower.includes('priority')
    ) {
      for (const line of section.content) {
        const itemMatch = line.trim().match(/^(?:\d+\.|[-*])\s+(.+)/);
        if (itemMatch && actionItems.length < 5) {
          actionItems.push(itemMatch[1].replace(/\*\*/g, ''));
        }
      }
      section._isActionItems = true;
    }
  }

  // If no explicit executive summary found, use first substantial paragraph
  if (!executiveSummary && sections.length > 0) {
    for (const section of sections) {
      if (section._isExecSummary || section._isActionItems) continue;
      const text = section.content
        .map(l => l.trim())
        .filter(l => l.length > 20 && !l.startsWith('-') && !l.startsWith('*') && !l.startsWith('|'))
        .slice(0, 2)
        .join(' ')
        .replace(/\*\*/g, '')
        .trim();
      if (text.length > 40) {
        executiveSummary = text;
        break;
      }
    }
  }

  // Build dimensions from remaining sections
  const dimensions = {};
  for (const section of sections) {
    if (section._isExecSummary || section._isActionItems) continue;

    const key = section.heading
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .replace(/_+$/, '');

    // Build summary from content
    const summaryLines = section.content
      .map(l => l.trim())
      .filter(l => l.length > 0 && !l.startsWith('#'));

    const summary = summaryLines
      .filter(l => !l.startsWith('|') && !l.startsWith('---'))
      .map(l => l.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, ''))
      .filter(l => l.length > 0)
      .join(' ')
      .replace(/\*\*/g, '')
      .trim();

    // Extract bullet points as detail items
    const detailItems = summaryLines
      .filter(l => l.match(/^[-*]\s+/) || l.match(/^\d+\.\s+/))
      .map(l => l.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '').replace(/\*\*/g, ''));

    // Detect risk level
    let risk_level = null;
    const riskBadge = section.badges.find(b => b.includes('RISK'));
    if (riskBadge) {
      risk_level = riskBadge.replace(/\s*RISK\s*/i, '').trim();
    }

    // Detect tipping risk
    let overall_tipping_risk = null;
    const tippingBadge = section.badges.find(b => b.includes('TIPPING'));
    if (tippingBadge) {
      overall_tipping_risk = tippingBadge.replace(/\s*TIPPING\s*RISK\s*/i, '').trim();
    }

    dimensions[key] = {
      score: section.score,
      summary,
      ...(risk_level && { risk_level }),
      ...(overall_tipping_risk && { overall_tipping_risk }),
      ...(detailItems.length > 0 && { detail_items: detailItems }),
    };
  }

  // Calculate overall grade if not found
  if (overallGrade == null) {
    const scores = Object.values(dimensions).map(d => d.score).filter(s => s != null);
    if (scores.length > 0) {
      overallGrade = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    }
  }

  return {
    player_name: title,
    overall_grade: overallGrade,
    executive_summary: executiveSummary,
    top_3_action_items: actionItems.slice(0, 5),
    dimensions,
  };
}

// ─── Reconstruct markdown from structured data (for export) ────────────────────

function reconstructMarkdown(data) {
  if (!data) return '';
  const lines = [];
  if (data.player_name) lines.push(`# ${data.player_name}\n`);
  if (data.overall_grade != null) lines.push(`**Overall Grade:** ${data.overall_grade}/100\n`);
  if (data.executive_summary) {
    lines.push(`## Executive Summary\n`);
    lines.push(`${data.executive_summary}\n`);
  }
  if (data.top_3_action_items?.length > 0) {
    lines.push(`## Top Action Items\n`);
    data.top_3_action_items.forEach((item, i) => lines.push(`${i + 1}. ${item}`));
    lines.push('');
  }
  if (data.dimensions) {
    for (const [key, dim] of Object.entries(data.dimensions)) {
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      lines.push(`## ${label}${dim.score != null ? ` (${dim.score}/100)` : ''}\n`);
      if (dim.risk_level) lines.push(`**Risk Level:** ${dim.risk_level}\n`);
      if (dim.summary) lines.push(`${dim.summary}\n`);
      if (dim.detail_items?.length > 0) {
        dim.detail_items.forEach(item => lines.push(`- ${item}`));
        lines.push('');
      }
    }
  }
  return lines.join('\n');
}

// ─── Dimension Card Component ──────────────────────────────────────────────────

function DimensionCard({ dimKey, dim, style, analysisMode }) {
  const [expanded, setExpanded] = useState(false);
  if (!dim || typeof dim !== 'object') return null;

  const score = dim.score;
  const hasStructuredDetails = dim.detail_items?.length > 0 || dim.tells_detected?.length > 0 ||
    dim.risk_factors?.length > 0 || dim.arsenal?.length > 0 ||
    dim.kinetic_chain || dim.strengths?.length > 0 || dim.inefficiencies?.length > 0;
  // Also show expand toggle if summary text is long enough to be clipped by line-clamp-4
  const hasLongSummary = dim.summary && dim.summary.length > 250;
  const hasDetails = hasStructuredDetails || hasLongSummary;

  return (
    <div className={`p-4 rounded-xl border ${style.border} ${style.bg}`}>
      {/* Header: dimension name + score */}
      <div className="flex items-center justify-between mb-2">
        <h4 className={`text-xs font-bold uppercase tracking-wider ${style.text}`}>{style.label}</h4>
        {score != null && (
          <span className={`text-lg font-bold ${style.text}`}>{Math.round(score)}</span>
        )}
      </div>

      {/* Progress bar */}
      {score != null && (
        <div className="w-full h-1.5 bg-background/50 rounded-full mb-3">
          <div
            className={`h-full rounded-full transition-all ${
              score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
            }`}
            style={{ width: `${Math.min(100, score)}%` }}
          />
        </div>
      )}

      {/* Risk/Grade badges */}
      <div className="flex flex-wrap gap-1 mb-2">
        {dim.risk_level && (
          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
            dim.risk_level === 'LOW' ? 'bg-green-500/20 text-green-400' :
            dim.risk_level === 'MODERATE' ? 'bg-yellow-500/20 text-yellow-400' :
            dim.risk_level === 'HIGH' ? 'bg-orange-500/20 text-orange-400' :
            'bg-red-500/20 text-red-400'
          }`}>
            {dim.risk_level} RISK
          </span>
        )}
        {dim.overall_tipping_risk && (
          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
            dim.overall_tipping_risk === 'LOW' || dim.overall_tipping_risk === 'NONE' ? 'bg-green-500/20 text-green-400' :
            dim.overall_tipping_risk === 'MEDIUM' ? 'bg-yellow-500/20 text-yellow-400' :
            'bg-red-500/20 text-red-400'
          }`}>
            {dim.overall_tipping_risk} TIPPING RISK
          </span>
        )}
        {/* Baseball-specific badges — only show for baseball analysis modes */}
        {(!analysisMode || !(analysisMode.startsWith('basketball') || analysisMode === 'deep-analysis-basketball' || analysisMode.startsWith('golf') || analysisMode === 'deep-analysis-golf' || analysisMode.startsWith('soccer') || analysisMode === 'deep-analysis-soccer')) && (
        <>
        {dim.kinetic_chain?.sequencing_grade && (
          <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/20 text-blue-400">
            Chain: {dim.kinetic_chain.sequencing_grade}
          </span>
        )}
        {dim.tunneling_grade && (
          <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-green-500/20 text-green-400">
            Tunnel: {dim.tunneling_grade}
          </span>
        )}
        {dim.sequencing_grade && (
          <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-cyan-500/20 text-cyan-400">
            Sequencing: {dim.sequencing_grade}
          </span>
        )}
        {dim.physical_maturity && (
          <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-purple-500/20 text-purple-400">
            {dim.physical_maturity}
          </span>
        )}
        {dim.velocity_upside && dim.velocity_upside !== 'none' && (
          <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-green-500/20 text-green-400">
            Velo: {dim.velocity_upside}
          </span>
        )}
        </>
        )}
        {/* Basketball-specific badges */}
        {(analysisMode?.startsWith('basketball') || analysisMode === 'deep-analysis-basketball') && (
        <>
          {dim.offensive_rating && (
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-yellow-500/20 text-yellow-400">
              OFF: {dim.offensive_rating}
            </span>
          )}
          {dim.defensive_rating && (
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/20 text-blue-400">
              DEF: {dim.defensive_rating}
            </span>
          )}
          {dim.efficiency && (
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-green-500/20 text-green-400">
              EFF: {dim.efficiency}
            </span>
          )}
          {dim.movement_grade && (
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-purple-500/20 text-purple-400">
              Movement: {dim.movement_grade}
            </span>
          )}
        </>
        )}
        {/* Golf-specific badges */}
        {(analysisMode?.startsWith('golf') || analysisMode === 'deep-analysis-golf') && (
        <>
          {dim.kinematic_sequence_grade && (
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400">
              Kinematic: {dim.kinematic_sequence_grade}
            </span>
          )}
          {dim.swing_efficiency && (
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-teal-500/20 text-teal-400">
              Swing Eff: {dim.swing_efficiency}
            </span>
          )}
          {dim.x_factor && (
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-green-500/20 text-green-400">
              X-Factor: {dim.x_factor}
            </span>
          )}
          {dim.ground_force_rating && (
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-lime-500/20 text-lime-400">
              GRF: {dim.ground_force_rating}
            </span>
          )}
          {dim.injury_risk_level && (
            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
              dim.injury_risk_level === 'LOW' ? 'bg-green-500/20 text-green-400' :
              dim.injury_risk_level === 'MODERATE' ? 'bg-yellow-500/20 text-yellow-400' :
              dim.injury_risk_level === 'HIGH' ? 'bg-orange-500/20 text-orange-400' :
              'bg-red-500/20 text-red-400'
            }`}>
              Injury: {dim.injury_risk_level}
            </span>
          )}
        </>
        )}
        {/* Soccer-specific badges */}
        {(analysisMode?.startsWith('soccer') || analysisMode === 'deep-analysis-soccer') && (
        <>
          {dim.pitch_control_rating && (
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/20 text-blue-400">
              Pitch Control: {dim.pitch_control_rating}
            </span>
          )}
          {dim.xg_estimate && (
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-sky-500/20 text-sky-400">
              xG: {dim.xg_estimate}
            </span>
          )}
          {dim.pressing_intensity && (
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-500/20 text-indigo-400">
              Press: {dim.pressing_intensity}
            </span>
          )}
          {dim.formation_grade && (
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-violet-500/20 text-violet-400">
              Formation: {dim.formation_grade}
            </span>
          )}
          {dim.injury_risk_level && (
            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
              dim.injury_risk_level === 'LOW' ? 'bg-green-500/20 text-green-400' :
              dim.injury_risk_level === 'MODERATE' ? 'bg-yellow-500/20 text-yellow-400' :
              dim.injury_risk_level === 'HIGH' ? 'bg-orange-500/20 text-orange-400' :
              'bg-red-500/20 text-red-400'
            }`}>
              Injury: {dim.injury_risk_level}
            </span>
          )}
        </>
        )}
      </div>

      {/* Key improvement area */}
      {(dim.glaring_improvement_area || dim.single_biggest_improvement) && (
        <p className="text-[11px] text-orange-300 font-medium mb-1">
          Key: {dim.glaring_improvement_area || dim.single_biggest_improvement}
        </p>
      )}

      {/* Summary */}
      {dim.summary && (
        <p className={cn(
          "text-xs text-foreground/80 leading-relaxed",
          !expanded && "line-clamp-4"
        )}>
          {dim.summary}
        </p>
      )}

      {/* Expandable details */}
      {hasDetails && (
        <>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[10px] text-primary hover:text-primary/80 mt-2 flex items-center gap-1"
          >
            {expanded ? 'Show Less' : (hasStructuredDetails ? 'Show Details' : 'Show More')}
            <svg className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {expanded && (
            <div className="mt-2 pt-2 border-t border-border/30 space-y-2 text-xs text-foreground/70">
              {/* Detail items (from markdown parsing) */}
              {dim.detail_items?.length > 0 && (
                <ul className="space-y-1 list-disc list-inside">
                  {dim.detail_items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              )}

              {/* Pitch tipping tells */}
              {dim.tells_detected?.length > 0 && (
                <div className="space-y-2">
                  {dim.tells_detected.map((tell, i) => (
                    <div key={i} className="p-2 bg-background/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                          tell.severity === 'CRITICAL' ? 'bg-red-500/20 text-red-400' :
                          tell.severity === 'HIGH' ? 'bg-orange-500/20 text-orange-400' :
                          tell.severity === 'MEDIUM' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>{tell.severity}</span>
                        <span className="font-medium text-foreground/90">{tell.tell_type?.replace(/_/g, ' ')}</span>
                      </div>
                      <p className="text-foreground/60">{tell.description}</p>
                      {tell.correction && <p className="text-green-400/80 mt-1">Fix: {tell.correction}</p>}
                    </div>
                  ))}
                </div>
              )}

              {/* Risk factors */}
              {dim.risk_factors?.length > 0 && (
                <div className="space-y-1">
                  {dim.risk_factors.map((rf, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold mt-0.5 ${
                        rf.severity === 'CRITICAL' ? 'bg-red-500/20 text-red-400' :
                        rf.severity === 'HIGH' ? 'bg-orange-500/20 text-orange-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>{rf.severity}</span>
                      <span>{rf.description || rf.factor?.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Arsenal */}
              {dim.arsenal?.length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {dim.arsenal.map((pitch, i) => (
                    <div key={i} className="p-2 bg-background/30 rounded-lg">
                      <div className="font-medium text-foreground/90">{pitch.pitch_type}</div>
                      {pitch.estimated_velocity_range && <div className="text-foreground/50">{pitch.estimated_velocity_range}</div>}
                      {pitch.command_grade && <span className="text-[9px] bg-blue-500/20 text-blue-400 px-1 rounded">{pitch.command_grade}</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Kinetic chain details */}
              {dim.kinetic_chain && (
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  {dim.kinetic_chain.hip_shoulder_separation_degrees != null && (
                    <div>Hip-Shoulder Sep: {dim.kinetic_chain.hip_shoulder_separation_degrees}°</div>
                  )}
                  {dim.kinetic_chain.stride_length_percent_height != null && (
                    <div>Stride: {dim.kinetic_chain.stride_length_percent_height}% height</div>
                  )}
                  {dim.kinetic_chain.energy_transfer_grade && (
                    <div>Energy Transfer: {dim.kinetic_chain.energy_transfer_grade}</div>
                  )}
                </div>
              )}

              {/* Strengths / Inefficiencies */}
              {dim.strengths?.length > 0 && (
                <div>
                  <span className="font-medium text-green-400/80">Strengths:</span>
                  <ul className="list-disc list-inside ml-2">
                    {dim.strengths.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}
              {dim.inefficiencies?.length > 0 && (
                <div>
                  <span className="font-medium text-orange-400/80">Inefficiencies:</span>
                  <ul className="list-disc list-inside ml-2">
                    {dim.inefficiencies.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}

              {/* Priority ranking */}
              {dim.priority_ranking?.length > 0 && (
                <div>
                  <span className="font-medium text-foreground/80">Priority Ranking:</span>
                  <ol className="list-decimal list-inside ml-2">
                    {dim.priority_ranking.map((p, i) => (
                      <li key={i}>{typeof p === 'string' ? p : p.description || p.issue || JSON.stringify(p)}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Export Dropdown ────────────────────────────────────────────────────────────

function ExportDropdown({ markdown, mediaItems }) {
  const [showMenu, setShowMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!showMenu) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (e) {
      console.error('Failed to copy to clipboard:', e);
    }
    setShowMenu(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <div className="flex items-center gap-2">
        {copySuccess && (
          <span className="text-xs text-green-400 flex items-center gap-1">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 13l4 4L19 7" /></svg>
            Copied!
          </span>
        )}
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/40 text-yellow-400 rounded-lg text-sm font-medium transition-all"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
          </svg>
          Export
          <svg className={`w-3 h-3 transition-transform ${showMenu ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </div>
      {showMenu && (
        <div className="absolute right-0 top-full mt-1 w-52 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-medium">Documents</div>
          {[
            { label: 'PDF', icon: 'text-red-400', fn: async () => { try { await exportToPDF(markdown); } catch (e) { console.error('PDF export failed:', e); } setShowMenu(false); } },
            { label: 'DOCX', icon: 'text-blue-400', fn: async () => { try { await exportToDOCX(markdown, mediaItems); } catch (e) { console.error('DOCX export failed:', e); } setShowMenu(false); } },
            { label: 'Markdown', icon: 'text-gray-400', fn: () => { try { exportToMarkdown(markdown); } catch (e) { console.error('Markdown export failed:', e); } setShowMenu(false); } },
            { label: 'Plain Text', icon: 'text-gray-500', fn: () => { try { exportToTXT(markdown); } catch (e) { console.error('TXT export failed:', e); } setShowMenu(false); } },
          ].map(({ label, icon, fn }) => (
            <button key={label} onClick={fn} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-left">
              <svg className={`w-4 h-4 ${icon}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
              </svg>
              {label}
            </button>
          ))}
          <div className="border-t border-gray-700" />
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-medium">Data</div>
          <button onClick={() => { try { exportToJSON(markdown, mediaItems); } catch (e) { console.error('JSON export failed:', e); } setShowMenu(false); }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-left">
            <svg className="w-4 h-4 text-yellow-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
            JSON
          </button>
          <button onClick={() => { try { exportToCSV(markdown, mediaItems); } catch (e) { console.error('CSV export failed:', e); } setShowMenu(false); }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-left">
            <svg className="w-4 h-4 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
            CSV
          </button>
          <div className="border-t border-gray-700" />
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-medium">Share</div>
          <button onClick={() => { try { exportViaEmail(markdown, mediaItems); } catch (e) { console.error('Email export failed:', e); } setShowMenu(false); }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-left">
            <svg className="w-4 h-4 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 6l-10 7L2 6" /></svg>
            Email
          </button>
          <button onClick={handleCopy} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors text-left">
            <svg className="w-4 h-4 text-purple-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
            Copy Text
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export function StructuredAnalysisReport({
  structuredData,
  markdown,
  analysisMode,
  model,
  title,
  mediaItems = [],
  analysisMetadata = null,
  onDrillDown,
  isDrillDownLoading,
  onClose,
  parseError,
}) {
  const [viewMode, setViewMode] = useState('cards');
  const [showMetadata, setShowMetadata] = useState(true);

  // Normalize data: structured JSON or parsed markdown
  const reportData = useMemo(() => {
    if (structuredData) return structuredData;
    if (markdown) return parseMarkdownToStructured(markdown, analysisMode);
    return null;
  }, [structuredData, markdown, analysisMode]);

  // Get color map for this analysis mode
  const colorMap = useMemo(() => MODE_COLOR_MAPS[analysisMode] || {}, [analysisMode]);

  // Build export markdown
  const exportMarkdown = useMemo(() => {
    if (markdown) return markdown;
    if (reportData) return reconstructMarkdown(reportData);
    return '';
  }, [markdown, reportData]);

  if (!reportData && !markdown) return null;

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    try {
      return new Date(ts).toLocaleString();
    } catch { return ts; }
  };

  // Card view
  const renderCards = () => {
    const data = reportData || {};
    const displayTitle = data.player_name || title || 'Video Analysis';
    const dimensionEntries = Object.entries(data.dimensions || {});

    return (
      <div className="px-4 py-6 max-w-5xl mx-auto w-full space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-foreground">{displayTitle}</h2>
            <p className="text-xs text-muted-foreground">
              {data.overall_grade != null && (
                <>
                  Overall Grade: <span className="text-primary font-bold text-sm">{data.overall_grade}/100</span>
                  <span className="mx-2">|</span>
                </>
              )}
              {model && <>Model: {model}</>}
              {parseError && <span className="ml-2 text-orange-400">(partial parse)</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExportDropdown markdown={exportMarkdown} mediaItems={mediaItems} />
            <button
              onClick={() => setViewMode(viewMode === 'cards' ? 'raw' : 'cards')}
              className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground border border-border/50 rounded-lg hover:bg-muted/30 transition-all"
            >
              {viewMode === 'cards' ? 'View Full Report' : 'View Cards'}
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
              >
                <XIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Data Analyzed Metadata */}
        {analysisMetadata && (
          <div className="p-3 bg-muted/20 border border-border/40 rounded-xl">
            <button
              onClick={() => setShowMetadata(!showMetadata)}
              className="flex items-center justify-between w-full text-left"
            >
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Data Analyzed
              </h3>
              <svg className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${showMetadata ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
              </svg>
            </button>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {analysisMetadata.timestamp && <span>Analyzed: {formatTimestamp(analysisMetadata.timestamp)}</span>}
              {analysisMetadata.modeName && <span>Mode: {analysisMetadata.modeName}</span>}
              {analysisMetadata.modelName && <span>Model: {analysisMetadata.modelName}</span>}
              {analysisMetadata.sport && <span>Sport: {analysisMetadata.sport}</span>}
              {analysisMetadata.userPrompt && <span className="italic truncate max-w-[300px]">Prompt: "{analysisMetadata.userPrompt}"</span>}
            </div>
            {showMetadata && (
              <div className="mt-3 space-y-2">
                {analysisMetadata.files?.length > 0 && (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b border-border/30">
                        <th className="py-1 pr-3">File</th>
                        <th className="py-1 pr-3">Type</th>
                        <th className="py-1 pr-3">Size</th>
                        <th className="py-1">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisMetadata.files.map((f, i) => (
                        <tr key={i} className="border-b border-border/20">
                          <td className="py-1 pr-3 font-mono truncate max-w-[200px]">{f.name}</td>
                          <td className="py-1 pr-3">{f.mediaType}</td>
                          <td className="py-1 pr-3">{formatFileSize(f.size)}</td>
                          <td className="py-1">{f.source || 'Upload'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {analysisMetadata.youtubeVideos?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">YouTube Videos</p>
                    {analysisMetadata.youtubeVideos.map((v, i) => (
                      <div key={i} className="text-xs text-muted-foreground flex gap-2 py-0.5">
                        <span className="font-mono truncate max-w-[250px]">{v.title || v.url}</span>
                        {v.channel && <span className="text-muted-foreground/60">({v.channel})</span>}
                      </div>
                    ))}
                  </div>
                )}
                {analysisMetadata.contextFiles?.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Context files: {analysisMetadata.contextFiles.map(f => f.name).join(', ')}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Executive Summary */}
        {data.executive_summary && (
          <div className="p-4 bg-card border border-border/50 rounded-xl">
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Executive Summary</h3>
            <p className="text-sm text-foreground leading-relaxed">{data.executive_summary}</p>
          </div>
        )}

        {/* Top Action Items */}
        {data.top_3_action_items?.length > 0 && (
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
            <h3 className="text-xs font-bold text-primary uppercase tracking-wider mb-2">Top Action Items</h3>
            <ol className="space-y-1.5">
              {data.top_3_action_items.map((item, i) => (
                <li key={i} className="text-sm text-foreground flex gap-2">
                  <span className="text-primary font-bold">{i + 1}.</span>
                  {item}
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Dimension Cards Grid */}
        {dimensionEntries.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {dimensionEntries.map(([key, dim], idx) => {
              const style = colorMap[key] ||
                { ...FALLBACK_COLORS[idx % FALLBACK_COLORS.length], label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) };
              return <DimensionCard key={key} dimKey={key} dim={dim} style={style} analysisMode={analysisMode} />;
            })}
          </div>
        )}

        {/* Drill-down section (for expert analysis) */}
        {onDrillDown && dimensionEntries.length > 0 && (
          <div className="flex items-center gap-2 pt-2">
            <span className="text-xs text-muted-foreground">Deep dive:</span>
            {dimensionEntries.slice(0, 3).map(([key]) => {
              const style = colorMap[key] || FALLBACK_COLORS[0];
              return (
                <button
                  key={key}
                  onClick={() => onDrillDown(key)}
                  disabled={isDrillDownLoading}
                  className={cn(
                    "px-3 py-1 text-xs rounded-lg border transition-all",
                    isDrillDownLoading
                      ? "bg-gray-600 text-gray-400 cursor-wait border-gray-600"
                      : `${style.bg} ${style.text} ${style.border} hover:opacity-80`
                  )}
                >
                  {isDrillDownLoading ? 'Analyzing...' : (colorMap[key]?.label || key.replace(/_/g, ' '))}
                </button>
              );
            })}
          </div>
        )}

        {/* Multilingual Export */}
        <ReportLanguageExport reportContent={exportMarkdown} analysisMode={analysisMode} />
      </div>
    );
  };

  return viewMode === 'cards' ? renderCards() : (
    <div className="px-4 py-6 max-w-5xl mx-auto w-full space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('cards')}
            className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground border border-border/50 rounded-lg hover:bg-muted/30 transition-all"
          >
            View Cards
          </button>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors">
            <XIcon className="w-5 h-5" />
          </button>
        )}
      </div>
      <AnalysisReport
        markdown={exportMarkdown}
        mediaItems={mediaItems}
        onDrillDown={onDrillDown}
        isDrillDownLoading={isDrillDownLoading}
        title={title}
      />
      <ReportLanguageExport reportContent={exportMarkdown} analysisMode={analysisMode} />
    </div>
  );
}

export default StructuredAnalysisReport;
